import './Page.css';

const subjects = [
  {
    icon: '📐',
    name: 'Mathematics',
    grades: 'Grades 6–12',
    topics: ['Pre-Algebra & Algebra I/II', 'Geometry', 'Pre-Calculus & Trigonometry', 'AP Calculus AB/BC', 'Statistics', 'SAT/ACT Math Foundations'],
  },
  {
    icon: '🔬',
    name: 'Science',
    grades: 'Grades 6–12',
    topics: ['Biology', 'Chemistry', 'Physics (Algebra & Calculus-based)', 'AP Physics 1 & 2', 'AP Chemistry', 'Earth & Environmental Science'],
  },
  {
    icon: '📖',
    name: 'English & Literature',
    grades: 'Grades 6–12',
    topics: ['Reading Comprehension', 'Literary Analysis & Essay Writing', 'Grammar & Mechanics', 'AP Language & Composition', 'AP Literature & Composition', 'ACT/SAT Reading & Writing'],
  },
  {
    icon: '🌍',
    name: 'History & Social Studies',
    grades: 'Grades 6–12',
    topics: ['US History', 'World History', 'AP US History (APUSH)', 'AP World History', 'Government & Civics', 'Economics'],
  },
  {
    icon: '🧮',
    name: 'Honors & Advanced Courses',
    grades: 'High School',
    topics: ['Honors Algebra & Geometry', 'Honors English & Literature', 'IB Mathematics', 'IB Physics & Chemistry', 'Multivariable Calculus', 'Linear Algebra'],
  },
  {
    icon: '📝',
    name: 'Middle School Core',
    grades: 'Grades 6–8',
    topics: ['Math (Pre-Algebra through Algebra I)', 'Language Arts & Writing', 'Earth Science & Life Science', 'US & World History', 'Study Skills & Organization'],
  },
];

const whyBoxes = [
  { icon: '🎯', title: 'Built Around Your Student', desc: 'Every session is tailored to your student\'s exact curriculum, learning style, and upcoming tests.' },
  { icon: '👨‍🏫', title: 'Subject-Specialist Tutors', desc: 'Our tutors hold degrees in their subject areas and have deep experience at the high school level and beyond.' },
  { icon: '📊', title: 'Progress Visible to Parents', desc: 'After every session, parents receive updates on what was covered, what improved, and what\'s next.' },
  { icon: '📅', title: 'Flexible Scheduling', desc: 'Weekday evenings and weekends via Zoom. Book sessions when you need them — before exams or ongoing.' },
  { icon: '💬', title: 'Direct Tutor Access', desc: 'Students and parents can message their tutor between sessions for quick questions and homework help.' },
  { icon: '📦', title: 'Bundle & Save', desc: 'Purchase 10-hour or 20-hour packages at a discounted rate. Use hours flexibly across sessions or subjects.' },
];

const testimonials = [
  { quote: 'Being in New York and taking the Regents exams was a tough challenge. Thanks to my tutor\'s guidance, I performed much better and passed with strong grades.', name: '— Rashika M., Student' },
  { quote: 'AP Physics used to feel so hard and confusing, but my tutor helped me break it down step by step. I actually started to enjoy it — and I got an A in the class.', name: '— Jordan T., Class of 2024' },
  { quote: 'I take sessions before exams, especially for math and physics. Those sessions make a big difference — I\'ve been able to keep my grade at an A.', name: '— Anika S., Student' },
  { quote: 'My daughter was never confident in her writing, but after two months of tutoring she really improved. Her grades went up and so did her confidence.', name: '— Preeti D., Parent' },
];

const faq = [
  { q: 'What subjects do you tutor?', a: 'We cover Math (Pre-Algebra through AP Calculus), Science (Biology, Chemistry, Physics, AP Sciences), English, History, and Honors/Advanced courses for Grades 6–12. If you don\'t see your subject, contact us — we likely have a tutor for it.' },
  { q: 'How does 1:1 subject tutoring work?', a: 'All sessions are live 1:1 via Zoom. Before the first session, we learn about your student\'s current grade, upcoming tests, and specific challenges. The tutor builds each session around those exact needs.' },
  { q: 'How often should my student meet with a tutor?', a: 'For ongoing grade support, once a week is typically enough. For intensive exam prep or catching up, two to three sessions per week for a focused period tends to be most effective.' },
  { q: 'Can we request the same tutor each time?', a: 'Yes — and we strongly encourage consistency. Building a relationship with one tutor who understands your student\'s learning style and goals makes every session more effective.' },
  { q: 'Are bundles available?', a: 'Yes. 10-hour and 20-hour packages are available at a discounted hourly rate. Hours can be split across multiple subjects if needed.' },
  { q: 'What if we need to cancel or reschedule?', a: 'We ask for 24-hour notice for cancellations. Our scheduling is flexible and we work with families to find consistent weekly times.' },
];

export default function SubjectTutoringPage() {
  return (
    <div className="page-wrap">

      {/* Hero */}
      <div className="sat-hero">
        <div className="container">
          <div className="exam-hero-badge">Academic Subject Tutoring</div>
          <h1>Expert tutors for every<br />subject, every grade.</h1>
          <p className="sat-hero-sub">
            Personalized live tutoring for Math, Science, English, History, and more.
            Expert tutors who know your student's curriculum and meet them where they are.
          </p>
          <p className="sat-hero-proof">★★★★★ &nbsp; Grades 6–12 · All subjects · Live 1:1 via Zoom</p>
          <div className="sat-hero-btns">
            <a href="https://secure.tutorcruncher.com/scholar-sphere/signup/client/" target="_blank" rel="noreferrer" className="btn-gold">Book a Free Consultation</a>
            <a href="#subjects" className="btn-outline-white">See All Subjects →</a>
          </div>
        </div>
      </div>

      {/* Stats strip */}
      <div className="exam-stats-strip">
        <div className="container">
          <div className="exam-stats-inner">
            <div>
              <div className="exam-stat-num">$55</div>
              <div className="exam-stat-label">Per Hour</div>
            </div>
            <div>
              <div className="exam-stat-num">6+</div>
              <div className="exam-stat-label">Subject Areas</div>
            </div>
            <div>
              <div className="exam-stat-num">6–12</div>
              <div className="exam-stat-label">Grade Levels</div>
            </div>
            <div>
              <div className="exam-stat-num">1:1</div>
              <div className="exam-stat-label">Live via Zoom</div>
            </div>
          </div>
        </div>
      </div>

      <div className="container page-body">

        {/* Pricing highlight */}
        <div className="page-section text-center">
          <div className="info-card highlight-card" style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--navy)', marginBottom: 8 }}>Tutoring Rate</div>
            <div style={{ fontSize: 48, fontWeight: 900, color: 'var(--navy)', lineHeight: 1 }}>$55<span style={{ fontSize: 22, fontWeight: 400 }}>/hour</span></div>
            <div style={{ fontSize: 14, color: 'var(--text-gray)', marginTop: 8, marginBottom: 20 }}>All subjects · All grade levels · Live 1:1 via Zoom</div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <div style={{ background: 'var(--off-white)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 18px', fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>Single session — $55</div>
              <div style={{ background: 'var(--off-white)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 18px', fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>10-hour bundle — save 10%</div>
              <div style={{ background: 'var(--off-white)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 18px', fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>20-hour bundle — save 15%</div>
            </div>
            <a href="https://secure.tutorcruncher.com/scholar-sphere/signup/client/" target="_blank" rel="noreferrer" className="btn-navy" style={{ display: 'inline-block', marginTop: 24 }}>Book Your First Session</a>
          </div>
        </div>

        {/* Subjects */}
        <div className="page-section" id="subjects">
          <div className="text-center" style={{ marginBottom: 32 }}>
            <h2 className="section-heading" style={{ marginBottom: 8 }}>Every subject. Every grade.</h2>
            <p className="section-sub">If it's on the syllabus, we have a tutor for it.</p>
          </div>
          <div className="tutor-grid">
            {subjects.map(s => (
              <div key={s.name} className="info-card" style={{ padding: 28 }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>{s.icon}</div>
                <h3 style={{ fontSize: 17, fontWeight: 800, color: 'var(--navy)', marginBottom: 4 }}>{s.name}</h3>
                <div style={{ fontSize: 12, color: 'var(--gold-dark)', fontWeight: 600, marginBottom: 12 }}>{s.grades}</div>
                <ul className="check-list" style={{ marginBottom: 0 }}>
                  {s.topics.map(t => <li key={t}>{t}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Why Scholar Sphere */}
        <div className="page-section">
          <div className="text-center" style={{ marginBottom: 32 }}>
            <h2 className="section-heading" style={{ marginBottom: 8 }}>Not just another tutoring marketplace.</h2>
            <p className="section-sub">Every tutor is vetted, every session is personalized, and parents are always in the loop.</p>
          </div>
          <div className="why-boxes">
            {whyBoxes.map(w => (
              <div key={w.title} className="why-box">
                <div className="why-box-icon">{w.icon}</div>
                <div className="why-box-title">{w.title}</div>
                <div className="why-box-desc">{w.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* How it works */}
        <div className="page-section">
          <div className="text-center" style={{ marginBottom: 32 }}>
            <h2 className="section-heading" style={{ marginBottom: 8 }}>From first call to first session in days.</h2>
          </div>
          <div className="two-col-section" style={{ marginBottom: 0 }}>
            <div className="info-card">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {[
                  { step: '01', title: 'Book a Free Consultation', desc: 'Tell us your subject, grade level, goals, and schedule. We\'ll match you with the right tutor.' },
                  { step: '02', title: 'Meet Your Tutor', desc: 'Your tutor reviews your student\'s curriculum and goals before the first session.' },
                  { step: '03', title: 'Start Your First Session', desc: 'Live 1:1 via Zoom. Your tutor dives straight into the material your student needs most.' },
                  { step: '04', title: 'Progress Every Week', desc: 'Parents receive updates after every session. Book additional sessions as needed.' },
                ].map(item => (
                  <div key={item.step} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--navy)', color: '#fff', fontWeight: 800, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{item.step}</div>
                    <div>
                      <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 4 }}>{item.title}</div>
                      <div style={{ fontSize: 14, color: 'var(--text-gray)', lineHeight: 1.55 }}>{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="info-card highlight-card">
              <h3 className="info-subhead">What's included in every session</h3>
              <ul className="check-list">
                <li>Live 1:1 tutoring via Zoom — no group sessions</li>
                <li>Tutor reviews student's curriculum before each session</li>
                <li>Homework help, concept explanations &amp; test prep</li>
                <li>Session summary shared with parent after every call</li>
                <li>Direct messaging with tutor between sessions</li>
                <li>Flexible rescheduling with 24-hour notice</li>
              </ul>
              <a href="https://secure.tutorcruncher.com/scholar-sphere/signup/client/" target="_blank" rel="noreferrer" className="btn-gold" style={{ display: 'inline-block', marginTop: 8 }}>Book a Free Consultation</a>
            </div>
          </div>
        </div>

        {/* Testimonials */}
        <div className="page-section">
          <div className="text-center" style={{ marginBottom: 28 }}>
            <h2 className="section-heading">Results that speak for themselves.</h2>
          </div>
          <div className="mini-test-grid">
            {testimonials.map((t, i) => (
              <div key={i} className="mini-test-card">
                <div style={{ color: 'var(--gold)', marginBottom: 8 }}>★★★★★</div>
                <p className="mini-test-quote">"{t.quote}"</p>
                <div className="mini-test-name">{t.name}</div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="page-section">
          <div className="text-center" style={{ marginBottom: 24 }}>
            <h2 className="section-heading">Frequently Asked Questions</h2>
          </div>
          <div className="faq-list">
            {faq.map((item, i) => (
              <details key={i} className="faq-item">
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="cta-strip" style={{marginBottom: 32}}>
          <h3>Ready to get started?</h3>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 15, marginBottom: 24 }}>
            Book a free 15-minute consultation. We'll match your student with the right tutor and get your first session scheduled within days.
          </p>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="https://secure.tutorcruncher.com/scholar-sphere/signup/client/" target="_blank" rel="noreferrer" className="btn-gold">Book a Free Consultation</a>
            <a href="mailto:contact@scholar-sphere.com" className="btn-outline-white">Email Us Directly</a>
          </div>
        </div>

      </div>
    </div>
  );
}
